"use client"

import useSWR from 'swr'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { soundsApi, LIST_COLUMNS, type Sound } from '@/lib/supabase'
import type { SearchRescue } from '@/lib/search-rescue'

// Page size — 24 per page for fast LCP; users paginate to see more
export const PAGE_SIZE = 24

export interface SoundFilters {
  category?: string
  search?: string
  sortBy?: 'created_at' | 'downloads' | 'title' | 'duration' | 'trending'
  sortOrder?: 'asc' | 'desc'
  collectionId?: string
  teslaModel?: string
  premiumOnly?: boolean
}

interface PaginatedSoundsResult {
  sounds: Sound[]
  isLoading: boolean
  /**
   * True while the current key (filters + offset) is fetching or revalidating.
   * Consumers can wait for it before treating an empty accumulated list as a
   * settled result or marking the current page loaded.
   */
  isValidating: boolean
  /**
   * True once a response (rows and total) exists for the current key. Unlike
   * `isLoading`/`isValidating` it does not lag a render behind a filter
   * change, so an empty `sounds` with `isSettled` false is "not here yet",
   * never "zero results".
   */
  isSettled: boolean
  /**
   * The search found nothing and a lookup for a corrected or trimmed query is
   * running. Until it answers, the page is waiting, not empty.
   */
  isRescuing: boolean
  /** Set when `sounds` come from a corrected or trimmed version of the search. */
  rescue: SearchRescue | null
  error: Error | null
  totalCount: number
  currentPage: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
  nextPage: () => void
  prevPage: () => void
  goToPage: (page: number) => void
  mutate: () => void
}

// soundsApi already retries (withRetry) and reports the final failure once.
// The global SWR config would capture it again and retry three more times, so
// one failed sort change would cost a dozen requests and a dozen events.
const QUIET_FAILURE = {
  onError: () => {},
  onErrorRetry: () => {},
}

// Serialize filters to a stable string for SWR key
function serializeFilters(filters: SoundFilters): string {
  return JSON.stringify(filters, Object.keys(filters).sort())
}

// Fetcher function for sounds
async function fetchSoundsPage(
  filters: SoundFilters,
  offset: number
): Promise<Sound[]> {
  const apiOptions: Parameters<typeof soundsApi.getSounds>[0] = {
    limit: PAGE_SIZE,
    offset,
    sortBy: filters.sortBy || 'downloads',
    sortOrder: filters.sortOrder || 'desc',
    columns: LIST_COLUMNS,
    // Client hook, opting in on purpose: without it a failed request comes
    // back as [] and the page reads it as "no results" (and logs it).
    throwOnError: true,
  }

  if (filters.category && filters.category !== 'All') {
    apiOptions.category = filters.category
  }

  if (filters.collectionId) {
    return soundsApi.getSoundsByCollection(filters.collectionId)
  }

  if (filters.search) {
    apiOptions.search = filters.search
  }

  if (filters.teslaModel) {
    apiOptions.compatibleModels = filters.teslaModel
  }

  if (filters.premiumOnly) {
    apiOptions.premiumOnly = true
  }

  return soundsApi.getSounds(apiOptions)
}

// Fetcher function for total count
async function fetchTotalCount(filters: SoundFilters): Promise<number> {
  // Collection doesn't support count in the same way
  if (filters.collectionId) {
    const sounds = await soundsApi.getSoundsByCollection(filters.collectionId)
    return sounds.length
  }

  return soundsApi.getSoundsCount({
    category: filters.category,
    search: filters.search,
    compatibleModels: filters.teslaModel,
    premiumOnly: filters.premiumOnly,
    sortBy: filters.sortBy,
    throwOnError: true,
  })
}

export interface UsePaginatedSoundsOptions {
  /** Seed page from a `?page=` URL param on first render (back-nav restore). */
  initialPage?: number
  /**
   * Called whenever currentPage settles on a new value (nextPage/prevPage/
   * goToPage, or a filter change resetting to page 1) so the caller can
   * mirror it into the URL — mirrors SearchFilters.updateURL's
   * router.push(..., { scroll: false }) pattern. Not called on first mount
   * (the initial value came from the URL already, re-pushing it would add a
   * redundant history entry).
   */
  onPageChange?: (page: number) => void
}

export function usePaginatedSounds(
  filters: SoundFilters,
  initialData?: { sounds?: Sound[]; count?: number },
  options: UsePaginatedSoundsOptions = {},
): PaginatedSoundsResult {
  const { initialPage = 1, onPageChange } = options

  // A search that finds nothing is retried once with a corrected or trimmed
  // query (X6 / LIB-05). The answer is kept per typed query and filter set, so
  // typing a new query or changing a filter starts from the typed text again.
  const typedKey = serializeFilters({
    search: filters.search,
    category: filters.category,
    teslaModel: filters.teslaModel,
    premiumOnly: filters.premiumOnly,
  })
  const [rescueState, setRescueState] = useState<{ key: string; rescue: SearchRescue | null } | null>(null)
  const rescueLookedUp = rescueState?.key === typedKey
  const rescue = rescueLookedUp ? rescueState.rescue : null
  const searchFilters = useMemo(
    () => (rescue ? { ...filters, search: rescue.term } : filters),
    [filters, rescue],
  )

  // Create a stable key based on filters
  const filterKey = useMemo(() => serializeFilters(searchFilters), [searchFilters])

  // Track pagination state including the filter key it belongs to
  // When filter key changes, page resets to 1
  const [paginationState, setPaginationState] = useState({
    currentPage: initialPage,
    filterKey: filterKey,
  })

  // Derive the actual current page - if filters changed, show page 1
  const currentPage = paginationState.filterKey === filterKey
    ? paginationState.currentPage
    : 1

  // Mirror currentPage out to the caller (URL) — but only when the page value
  // actually changes. `onPageChange` is typically a useCallback over
  // `searchParams`, so its identity churns on every URL change; keeping it in
  // the dependency array re-pushed the current page for URL changes this hook
  // did not cause, which was half of the /sounds replaceState loop (TES-1872).
  // Seeding the ref with `currentPage` also covers the first run: that value
  // was just read *from* the URL via initialPage, so re-pushing it would add a
  // redundant history entry.
  const onPageChangeRef = useRef(onPageChange)
  useEffect(() => {
    onPageChangeRef.current = onPageChange
  }, [onPageChange])

  const lastMirroredPageRef = useRef(currentPage)
  useEffect(() => {
    if (lastMirroredPageRef.current === currentPage) return
    lastMirroredPageRef.current = currentPage
    onPageChangeRef.current?.(currentPage)
  }, [currentPage])

  // Calculate offset based on current page
  const offset = (currentPage - 1) * PAGE_SIZE

  // Only use server-rendered initialData as fallback when no active filters AND the
  // default sort are applied. The server payload is ordered by downloads/desc; seeding
  // it for any other sort (e.g. "Newest") would pin the grid to the wrong order and
  // make the sort dropdown appear to do nothing.
  const isDefaultSort =
    (!searchFilters.sortBy || searchFilters.sortBy === 'downloads') &&
    (!searchFilters.sortOrder || searchFilters.sortOrder === 'desc')
  const isDefaultFilters =
    !searchFilters.search &&
    (!searchFilters.category || searchFilters.category === 'All') &&
    !searchFilters.teslaModel &&
    !searchFilters.collectionId &&
    !searchFilters.premiumOnly &&
    isDefaultSort

  // Fetch sounds for current page
  const {
    data: sounds,
    error: soundsError,
    isLoading: soundsLoading,
    isValidating: soundsValidating,
    mutate: mutateSounds,
  } = useSWR(
    { type: 'sounds', filterKey, offset },
    async (key) => {
      const parsedFilters = JSON.parse(key.filterKey) as SoundFilters
      return fetchSoundsPage(parsedFilters, key.offset)
    },
    {
      revalidateOnFocus: false,
      keepPreviousData: false,
      dedupingInterval: 5000,
      ...QUIET_FAILURE,
      fallbackData: isDefaultFilters && offset === 0 ? initialData?.sounds : undefined,
    }
  )

  // Fetch total count (separate query, cached)
  const {
    data: totalCount,
    error: countError,
    mutate: mutateCount,
  } = useSWR(
    { type: 'count', filterKey },
    async (key) => {
      const parsedFilters = JSON.parse(key.filterKey) as SoundFilters
      return fetchTotalCount(parsedFilters)
    },
    {
      revalidateOnFocus: false,
      keepPreviousData: false,
      dedupingInterval: 10000, // Cache count longer
      ...QUIET_FAILURE,
      fallbackData: isDefaultFilters ? initialData?.count : undefined,
    }
  )

  const settled = sounds !== undefined && totalCount !== undefined
  const wantsRescue =
    Boolean(filters.search) &&
    !filters.collectionId &&
    !rescueLookedUp &&
    settled &&
    totalCount === 0 &&
    !soundsError &&
    !countError

  useEffect(() => {
    if (!wantsRescue || !filters.search) return
    let cancelled = false
    soundsApi
      .getSearchRescue({
        search: filters.search,
        category: filters.category,
        compatibleModels: filters.teslaModel,
        premiumOnly: filters.premiumOnly,
      })
      .catch(() => null)
      .then((found) => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- stores the answer of an async lookup keyed to the query it was asked for
        if (!cancelled) setRescueState({ key: typedKey, rescue: found })
      })
    return () => {
      cancelled = true
    }
  }, [wantsRescue, typedKey, filters.search, filters.category, filters.teslaModel, filters.premiumOnly])

  // Calculate pagination values
  const total = totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1

  // Navigation functions - always update with current filterKey
  const nextPage = useCallback(() => {
    if (hasNextPage) {
      setPaginationState((prev) => ({
        filterKey,
        currentPage: prev.filterKey === filterKey ? prev.currentPage + 1 : 2,
      }))
    }
  }, [hasNextPage, filterKey])

  const prevPage = useCallback(() => {
    if (hasPrevPage) {
      setPaginationState((prev) => ({
        filterKey,
        currentPage: prev.filterKey === filterKey ? prev.currentPage - 1 : 1,
      }))
    }
  }, [hasPrevPage, filterKey])

  const goToPage = useCallback(
    (page: number) => {
      const validPage = Math.max(1, Math.min(page, totalPages))
      setPaginationState({
        filterKey,
        currentPage: validPage,
      })
    },
    [totalPages, filterKey]
  )

  // Combined mutate function
  const mutate = useCallback(() => {
    mutateSounds()
    mutateCount()
  }, [mutateSounds, mutateCount])

  return {
    sounds: sounds ?? [],
    isLoading: soundsLoading && !sounds,
    isValidating: soundsValidating,
    isSettled: settled && !wantsRescue,
    isRescuing: wantsRescue,
    rescue,
    error: soundsError || countError || null,
    totalCount: total,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    nextPage,
    prevPage,
    goToPage,
    mutate,
  }
}
